import { Card, CardBody, Divider, Flex, Stack, Text } from '@chakra-ui/react';
import {
  CurrentUserDocument,
  useUpdateUserSettingsMutation,
} from '@client/graphql/__generated__/main-operations';
import { ProductFeatures } from '@client/graphql/__generated__/types';
import {
  ChartColumnIcon,
  DumbbellIcon,
  GaugeIcon,
  HomeIcon,
  MapIcon,
  MedalIcon,
  UserIcon,
  UsersRoundIcon,
} from 'lucide-react';
import { FC } from 'react';
import { useRegisteredRoles } from '~/apps/consumer/hooks/useRegisteredRoles';
import { useProdFeatureFlagEnabled } from '~/common/hooks/useProdFeatureFlagEnabled';
import { useAppContext } from '~/services/main/contexts/AppContext';
import {
  FeatureKey,
  useIsFeatureEnabledForPayload,
} from '../../hooks/useIsFeatureEnabledForPayload';
import { NavButton, NavButtonProps } from '../Navigation/NavButton';
import { OrganizationSelect } from '../Organization/OrganizationSelect';
import { SIDEBAR_WIDTH_EXPANDED } from '../PageDetailLayout/SidebarItem';
import { NavGroupLabel } from './NavGroupLabel';

export const NavItems: FC<
  NavButtonProps & { setIsExpanded?: (expanded: boolean) => void }
> = ({ isExpanded, onClick }) => {
  const {
    user,
    selectedOrganization,
    isSelectedOrgAdmin,
    organizations,
    isFeatureSupported,
  } = useAppContext();
  const { isRegisteredAgent } = useRegisteredRoles();

  // const isBuyerAgencyAgreementEnabled = useProdFeatureFlagEnabled(
  //   'buyer-agency-agreement'
  // );

  // Only want to show the org scorecards nav item if the org is whitelisted
  const isOfferScorecardEnabledForOrg = useIsFeatureEnabledForPayload({
    featureFlagKey: FeatureKey.OFFER_COACH,
    organizationId: selectedOrganization?.id,
  });

  // Can show the agent scorecards nav item if the user or org is whitelisted
  const isOfferScorecardEnabledForUserOrOrg = useIsFeatureEnabledForPayload({
    featureFlagKey: FeatureKey.OFFER_COACH,
    organizationId: selectedOrganization?.id,
    userId: user?.id,
  });

  const isOffMarketListingsEnabled = useProdFeatureFlagEnabled(
    'off-market-listings'
  );

  const [updateUserSettings] = useUpdateUserSettingsMutation({
    refetchQueries: [CurrentUserDocument],
  });

  const iconSize = {
    width: '16px',
    height: '16px',
  };

  return (
    <Stack
      alignItems="flex-start"
      flexDir="column"
      height={{ base: '100vh', md: 'auto' }}
      justifyContent={{ base: 'flex-start', md: 'center' }}
      width="100%"
    >
      {!!selectedOrganization && (
        <>
          <Flex
            overflow="hidden"
            pt={2}
            px={4}
            visibility={isExpanded ? 'visible' : 'hidden'}
            width={SIDEBAR_WIDTH_EXPANDED}
          >
            {organizations?.length && organizations?.length > 1 ? (
              <OrganizationSelect
                organizations={organizations}
                selectedOrganizationId={selectedOrganization?.id || undefined}
                onChange={(value) =>
                  void updateUserSettings({
                    variables: {
                      input: {
                        selectedOrganizationId: value,
                      },
                    },
                  })
                }
              />
            ) : (
              <Card width="100%">
                <CardBody display="flex" justifyContent="center" p={2}>
                  <Text>{selectedOrganization.name}</Text>
                </CardBody>
              </Card>
            )}
          </Flex>
          {isSelectedOrgAdmin && (
            <>
              {/* <NavButton
                isExpanded={isExpanded}
                leftIcon={<ChartColumnIncreasingIcon {...iconSize} />}
                to="/organizations/overview"
                onClick={onClick}
              >
                {isExpanded && <Text fontWeight={500}>Overview</Text>}
              </NavButton> */}
              {isOfferScorecardEnabledForOrg && (
                <>
                  <NavButton
                    isExpanded={isExpanded}
                    leftIcon={<ChartColumnIcon {...iconSize} />}
                    to="/organizations/pipeline"
                    onClick={onClick}
                  >
                    {isExpanded && <Text fontWeight={500}>Pipeline</Text>}
                  </NavButton>
                  <NavButton
                    isExpanded={isExpanded}
                    leftIcon={<MedalIcon {...iconSize} />}
                    to="/organizations/performance"
                    onClick={onClick}
                  >
                    {isExpanded && <Text fontWeight={500}>Performance</Text>}
                  </NavButton>
                  {isFeatureSupported(ProductFeatures.MARKET_COMPARISON) && (
                    <NavButton
                      isExpanded={isExpanded}
                      leftIcon={<DumbbellIcon {...iconSize} />}
                      to="/organizations/offer-benchmarks"
                      onClick={onClick}
                    >
                      {isExpanded && <Text fontWeight={500}>Benchmarks</Text>}
                    </NavButton>
                  )}

                  <NavButton
                    isExpanded={isExpanded}
                    leftIcon={<GaugeIcon {...iconSize} />}
                    to="/organizations/offer-scorecards"
                    onClick={onClick}
                  >
                    {isExpanded && (
                      <Text fontWeight={500}>Team Scorecards</Text>
                    )}
                  </NavButton>
                </>
              )}
              <NavButton
                isExpanded={isExpanded}
                leftIcon={<UserIcon {...iconSize} />}
                to="/organizations/admin"
                onClick={onClick}
              >
                {isExpanded && <Text fontWeight={500}>Admin</Text>}
              </NavButton>
            </>
          )}
        </>
      )}
      {!!selectedOrganization && isSelectedOrgAdmin && (
        <Divider
          borderColor="whiteAlpha.300"
          my={!!selectedOrganization ? 4 : 0}
          orientation="horizontal"
          width="100%"
        />
      )}
      {!!selectedOrganization && isSelectedOrgAdmin && (
        <NavGroupLabel isExpanded={isExpanded}>Agent</NavGroupLabel>
      )}
      {/* {isRegisteredAgent && isBuyerAgencyAgreementEnabled && (
        <NavButton
          isExpanded={isExpanded}
          leftIcon={<LayoutGridIcon {...iconSize} />}
          to="/dashboard"
          onClick={onClick}
        >
          {isExpanded && <Text fontWeight={500}>Dashboard</Text>}
        </NavButton>
      )} */}
      {isRegisteredAgent && (
        <NavButton
          isExpanded={isExpanded}
          leftIcon={<MapIcon {...iconSize} />}
          to="/map"
          onClick={onClick}
        >
          {isExpanded && (
            <Flex>
              <Text fontWeight={500}>Map</Text>
            </Flex>
          )}
        </NavButton>
      )}
      {isOfferScorecardEnabledForUserOrOrg && isRegisteredAgent && (
        <NavButton
          isExpanded={isExpanded}
          leftIcon={<GaugeIcon {...iconSize} />}
          to="/offer-scorecards"
          onClick={onClick}
        >
          {isExpanded && <Text fontWeight={500}>Offer Scorecards</Text>}
        </NavButton>
      )}
      {isOffMarketListingsEnabled &&
        (user?.hasListings || isRegisteredAgent) && (
          <NavButton
            isExpanded={isExpanded}
            leftIcon={<HomeIcon {...iconSize} />}
            to="/listings"
            onClick={onClick}
          >
            {isExpanded && <Text fontWeight={500}>My Listings</Text>}
          </NavButton>
        )}
      {isRegisteredAgent && (
        <NavButton
          isExpanded={isExpanded}
          leftIcon={<UsersRoundIcon {...iconSize} />}
          to="/buyers"
          onClick={onClick}
        >
          {isExpanded && <Text fontWeight={500}>Buyers</Text>}
        </NavButton>
      )}
      {/* <NavItemAlerts
        iconSize={iconSize}
        isExpanded={isExpanded}
        onClick={onClick}
      /> */}
    </Stack>
  );
};
